import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { api } from '../../services/api';
import GuideModal, { itemGuide } from './GuideModal';

/* ── Constantes ── */
const STATUS_LABELS = {
  all:       'Todos',
  pending:   'Pendiente',
  accepted:  'Aceptado',
  preparing: 'Preparando',
  served:    'Servido',
  cancelled: 'Cancelado',
  paid:      'Pagado',
};

const KANBAN_COLS = [
  { id: 'incoming', label: 'Entrante',   statuses: ['pending', 'accepted'], accent: 'var(--a-pink)' },
  { id: 'kitchen',  label: 'En cocina',  statuses: ['preparing'],           accent: 'var(--a-cyan)' },
  { id: 'ready',    label: 'Listo',      statuses: ['served'],              accent: 'var(--a-green)' },
];

const NEXT_STATUS = { pending: 'accepted', accepted: 'preparing', preparing: 'served', served: 'paid' };
const NEXT_LABEL  = { pending: 'Aceptar', accepted: 'Preparar',  preparing: 'Servido', served: 'Cobrado' };

/* ── Helpers ── */
function ageMin(order) {
  const ref = order.acceptedAt || order.createdAt;
  if (!ref) return 0;
  return Math.floor((Date.now() - new Date(ref).getTime()) / 60000);
}

function urgencyClass(order) {
  const min = ageMin(order);
  if (order.status === 'served') return '';
  if (min >= 15) return 'ko-urgent';
  if (min >= 10) return 'ko-warn';
  return '';
}

function formatTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

function formatDateTime(value) {
  if (!value) return 'Sin fecha';
  return new Date(value).toLocaleString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function tableLabel(order) {
  return `Mesa ${order.tableId?.number ?? '?'}`;
}

function tableKey(order) {
  return String(order.tableId?._id || order.tableId || '');
}

function orderMatchesSearch(order, query) {
  if (!query.trim()) return true;
  const text = [
    order._id,
    tableLabel(order),
    order.status,
    order.customerNote,
    ...(order.items || []).flatMap((i) => [i.nameSnapshot, i.notes]),
  ].filter(Boolean).join(' ').toLowerCase();
  return text.includes(query.trim().toLowerCase());
}

/* ── Tarjeta Kanban ── */
function KanbanCard({ order, onAction, onCancel, onGuide, reduced }) {
  const age   = ageMin(order);
  const uCls  = urgencyClass(order);
  const next  = NEXT_STATUS[order.status];
  const total = Number(order.total || 0).toFixed(2);

  const cardMotion = reduced ? {} : {
    initial:  { opacity: 0, y: 10 },
    animate:  { opacity: 1, y: 0 },
    exit:     { opacity: 0, scale: 0.95 },
    transition: { duration: 0.18 },
  };

  return (
    <motion.div className={`ko-card ${uCls}`} layout {...cardMotion}>
      <div className="ko-card-head">
        <span className="ko-mesa">{tableLabel(order)}</span>
        <span className="ko-age">{age}m</span>
        {order.source === 'staff' && <span className="ko-source-badge">staff</span>}
      </div>

      <ul className="ko-items">
        {(order.items || []).slice(0, 5).map((it, idx) => {
          const guide = itemGuide(it);
          return (
            <li key={idx}>
              <span className="ko-qty">{it.quantity}×</span>
              <span className="ko-name">{it.nameSnapshot}</span>
              {guide && (
                <button type="button" className="ko-btn-guide" onClick={() => onGuide(guide)} title={`Guía de ${guide.name}`}>
                  Guía
                </button>
              )}
              {it.notes && <em className="ko-note">{it.notes}</em>}
            </li>
          );
        })}
        {(order.items || []).length > 5 && (
          <li className="ko-more">+{order.items.length - 5} más</li>
        )}
      </ul>

      {order.customerNote && (
        <p className="ko-customer-note">💬 {order.customerNote}</p>
      )}

      <div className="ko-card-foot">
        <span className="ko-total">{total} €</span>
        <span className="ko-time">{formatTime(order.createdAt)}</span>
      </div>

      <div className="ko-card-actions">
        {next && (
          <button type="button" className="ko-btn-next" onClick={() => onAction(order._id, next)}>
            {NEXT_LABEL[order.status]}
          </button>
        )}
        {!['cancelled', 'paid'].includes(order.status) && (
          <button type="button" className="ko-btn-cancel" onClick={() => onCancel(order._id)}>
            ✕
          </button>
        )}
      </div>
    </motion.div>
  );
}

/* ── Vista Kanban ── */
function KanbanView({ orders, onAction, onCancel, onGuide }) {
  const reduced = useReducedMotion();

  return (
    <div className="ko-board">
      {KANBAN_COLS.map((col) => {
        const colOrders = orders.filter((o) => col.statuses.includes(o.status));
        return (
          <div key={col.id} className="ko-col">
            <div className="ko-col-head" style={{ '--col-accent': col.accent }}>
              <span className="ko-col-label">{col.label}</span>
              <span className="ko-col-count">{colOrders.length}</span>
            </div>
            <div className="ko-col-body">
              <AnimatePresence mode="popLayout">
                {colOrders.length === 0 ? (
                  <div className="ko-col-empty">Sin pedidos</div>
                ) : (
                  colOrders.map((o) => (
                    <KanbanCard
                      key={o._id}
                      order={o}
                      onAction={onAction}
                      onCancel={onCancel}
                      onGuide={onGuide}
                      reduced={reduced}
                    />
                  ))
                )}
              </AnimatePresence>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Vista Historial (lista completa) ── */
function HistoryView({ orders, onAction }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [tableFilter,  setTableFilter]  = useState('all');
  const [dateFrom,     setDateFrom]      = useState('');
  const [dateTo,       setDateTo]        = useState('');
  const [search,       setSearch]        = useState('');

  const tableOptions = useMemo(() => {
    const map = new Map();
    for (const o of orders) {
      const k = tableKey(o);
      if (k) map.set(k, tableLabel(o));
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1], 'es', { numeric: true }));
  }, [orders]);

  const filtered = useMemo(() => {
    const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const to   = dateTo   ? new Date(`${dateTo}T23:59:59.999`) : null;
    return orders.filter((o) => {
      if (statusFilter !== 'all' && o.status !== statusFilter) return false;
      if (tableFilter  !== 'all' && tableKey(o) !== tableFilter) return false;
      const created = o.createdAt ? new Date(o.createdAt) : null;
      if (from && (!created || created < from)) return false;
      if (to   && (!created || created > to))   return false;
      return orderMatchesSearch(o, search);
    });
  }, [orders, statusFilter, tableFilter, dateFrom, dateTo, search]);

  const summary = useMemo(() =>
    filtered.reduce((acc, o) => {
      acc.count++;
      if (o.status !== 'cancelled') acc.total += Number(o.total || 0);
      acc[o.status] = (acc[o.status] || 0) + 1;
      return acc;
    }, { count: 0, total: 0 }),
    [filtered]
  );

  return (
    <>
      <section className="neon-orders-tools" aria-label="Filtros de historial">
        <div className="neon-orders-filter">
          <label htmlFor="hist-status">Estado</label>
          <select id="hist-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="neon-orders-filter">
          <label htmlFor="hist-table">Mesa</label>
          <select id="hist-table" value={tableFilter} onChange={(e) => setTableFilter(e.target.value)}>
            <option value="all">Todas</option>
            {tableOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="neon-orders-filter">
          <label htmlFor="hist-from">Desde</label>
          <input id="hist-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div className="neon-orders-filter">
          <label htmlFor="hist-to">Hasta</label>
          <input id="hist-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <div className="neon-orders-filter neon-orders-search">
          <label htmlFor="hist-search">Buscar</label>
          <input id="hist-search" type="search" placeholder="Producto, nota, pedido…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <button type="button" className="neon-orders-reset"
          onClick={() => { setStatusFilter('all'); setTableFilter('all'); setDateFrom(''); setDateTo(''); setSearch(''); }}>
          Limpiar
        </button>
      </section>

      <section className="neon-orders-summary" aria-label="Resumen">
        <div><span>Mostrados</span><strong>{summary.count}</strong></div>
        <div><span>Total</span><strong>{summary.total.toFixed(2)} €</strong></div>
        <div><span>Pendientes</span><strong>{summary.pending || 0}</strong></div>
        <div><span>Servidos</span><strong>{summary.served || 0}</strong></div>
      </section>

      <div className="neon-orders">
        {filtered.length === 0 ? (
          <div className="neon-orders-empty">No hay pedidos con los filtros seleccionados.</div>
        ) : filtered.map((o) => (
          <div key={o._id} className="neon-order-card">
            <div className="neon-order-header">
              <div className="neon-order-head-main">
                <span className="neon-order-table">{tableLabel(o)}</span>
                <span className="neon-order-date">{formatDateTime(o.createdAt)}</span>
              </div>
              <span className={`neon-order-status neon-order-status--${o.status}`}>
                {STATUS_LABELS[o.status] || o.status}
              </span>
              <span className="neon-order-total">{Number(o.total || 0).toFixed(2)} €</span>
            </div>
            <ul className="neon-order-items">
              {(o.items || []).map((it, idx) => (
                <li key={idx}>
                  <span>{it.quantity}× {it.nameSnapshot}</span>
                  <strong>{(Number(it.priceSnapshot || 0) * Number(it.quantity || 1)).toFixed(2)} €</strong>
                  {it.notes && <em>{it.notes}</em>}
                </li>
              ))}
            </ul>
            {o.customerNote && <p className="neon-order-note">Nota: {o.customerNote}</p>}
            <div className="neon-order-actions">
              {NEXT_STATUS[o.status] && (
                <button type="button" onClick={() => onAction(o._id, NEXT_STATUS[o.status])}>
                  {NEXT_LABEL[o.status]}
                </button>
              )}
              {!['cancelled', 'paid'].includes(o.status) && (
                <button type="button" className="neon-btn-cancel" onClick={() => onAction(o._id, 'cancelled')}>
                  Cancelar
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* ── Componente principal ── */
export default function AdminOrders() {
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [view,    setView]    = useState('kanban'); // 'kanban' | 'list'
  const [guide,   setGuide]   = useState(null);     // { name, steps } | null

  const load = () =>
    api.get('/orders')
       .then((data) => { setOrders(Array.isArray(data) ? data : []); setError(''); })
       .catch((err) => setError(err.message || 'No se pudieron cargar los pedidos'))
       .finally(() => setLoading(false));

  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, []);

  const changeStatus = async (id, status) => {
    await api.patch(`/orders/${id}/status`, { status });
    load();
  };

  const cancelOrder = (id) => changeStatus(id, 'cancelled');

  /* Pedidos activos para kanban */
  const activeOrders = useMemo(
    () => orders.filter((o) => ['pending', 'accepted', 'preparing', 'served'].includes(o.status)),
    [orders]
  );

  const pendingCount = orders.filter((o) => o.status === 'pending').length;

  return (
    <div className="neon-admin-page">
      {/* Cabecera */}
      <div className="neon-admin-page-head">
        <div>
          <h2>
            Pedidos
            {pendingCount > 0 && (
              <span className="ko-pending-pill">{pendingCount} nuevo{pendingCount > 1 ? 's' : ''}</span>
            )}
            <span className="neon-poll"> (auto-refresh 10s)</span>
          </h2>
        </div>
        {/* Toggle vista */}
        <div className="ko-view-toggle">
          <button
            type="button"
            className={`ko-view-btn${view === 'kanban' ? ' active' : ''}`}
            onClick={() => setView('kanban')}
          >
            Servicio
          </button>
          <button
            type="button"
            className={`ko-view-btn${view === 'list' ? ' active' : ''}`}
            onClick={() => setView('list')}
          >
            Historial
          </button>
        </div>
      </div>

      {error && <div className="neon-orders-error">{error}</div>}

      {loading ? (
        <div className="neon-orders-empty">Cargando pedidos…</div>
      ) : view === 'kanban' ? (
        <KanbanView orders={activeOrders} onAction={changeStatus} onCancel={cancelOrder} onGuide={setGuide} />
      ) : (
        <HistoryView orders={orders} onAction={changeStatus} />
      )}

      {guide && <GuideModal guide={guide} onClose={() => setGuide(null)} />}
    </div>
  );
}
